// src/components/RegistryChat.tsx
//
// Counterparty-facing chat — the "Chat with us here" path from
// Footer.tsx, rendered inside RegistryView.tsx when ?chat=true is
// present. Reuses useDashboardChat with mode: 'counterparty' and the
// entity uid from the URL as targetEntityId — same underlying chat.ts
// mechanism ChatScreen.tsx uses, different auth branch. Deliberately a
// much smaller UI than ChatScreen.tsx: no history Drawer, no new-chat
// button (a visitor reaching a specific entity has no reason to browse
// past unrelated conversations), no resume-on-mount (every visit here
// starts a fresh conversation with this entity by design).
//
// Sign-in gate: a first-time visitor has no Firebase session at all.
// Deliberately client-side only (signInWithEmailAndPassword /
// createUserWithEmailAndPassword directly, mirroring LoginPage.tsx's
// non-resumeSession path) — no server-side round trip needed to
// establish this. Once auth.currentUser is populated,
// subscribeToAuthChanges fires and this component re-renders past the
// gate automatically; useDashboardChat's existing authedFetch/
// getDashboardToken (POST /dashboard/session) already accepts any
// verified Firebase ID token regardless of whether the caller owns an
// entity, so no new backend endpoint was needed for this at all.
//
// UPDATED — two UX fixes, both confirmed against real shadcn/Base UI
// component source before merging (dialog.tsx and alert-dialog.tsx
// both pasted and reviewed directly this pass):
//   1. RegistrySignInGate now uses the real Input/Label primitives
//      (matching DashboardOutcomeDetail.tsx / DashboardProfile.tsx's
//      existing pattern) instead of hand-rolled <input> elements — a
//      visitor arriving at a direct registry link with no session
//      previously saw a visually inconsistent form versus every other
//      form in the product.
//   2. "Don't send" on a pending drafted message now routes through a
//      confirm step (AlertDialog) instead of firing resolveApproval(false)
//      immediately — matches the existing codebase convention for
//      irreversible/easy-to-regret actions (DashboardOutcomeDetail.tsx's
//      outcome/rule deletion uses the identical shared-instance,
//      boolean-driven AlertDialog pattern). Confirmed AlertDialogAction
//      is a thin Button wrapper (accepts variant via spread) and
//      AlertDialogCancel defaults its own variant to "outline" — no
//      assumptions left unconfirmed against the real component source
//      for either of these two changes.

import * as React from "react"
import { useState, useEffect } from "react"
import ReactMarkdown from "react-markdown"
import { signInWithEmailAndPassword, createUserWithEmailAndPassword } from "firebase/auth"
import { auth, subscribeToAuthChanges } from "../services/firebase"
import {
  MessageScrollerProvider,
  MessageScroller,
  MessageScrollerViewport,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerButton,
} from "./ui/message-scroller"
import { MessageGroup, Message, MessageContent } from "./ui/message"
import { Bubble, BubbleContent } from "./ui/bubble"
import { Marker, MarkerIcon, MarkerContent } from "./ui/marker"
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Label } from "./ui/label"
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "./ui/alert-dialog"
import { Loader2Icon, InfoIcon } from "lucide-react"
import { useDashboardChat, type ChatMessage } from "../hooks/useDashboardChat"
import {
  Field,
  FieldGroup,
  FieldLabel,
  FieldError,
} from "./ui/field"

function stripEmoji(text: string): string {
  return text.replace(
    /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{2190}-\u{21FF}\u{2B00}-\u{2BFF}\u{FE0F}]/gu,
    ""
  )
}

function TextBlockBubble({ text, align }: { text: string; align: "start" | "end" }) {
  const cleaned = stripEmoji(text)
  if (!cleaned.trim()) return null
  return (
    <Bubble align={align} variant={align === "end" ? "default" : "secondary"}>
      <BubbleContent>
        <div className="prose prose-sm dark:prose-invert max-w-none text-inherit *:text-inherit [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
          <ReactMarkdown>{cleaned}</ReactMarkdown>
        </div>
      </BubbleContent>
    </Bubble>
  )
}

function ToolUseMarker({ block }: { block: { name: string } }) {
  return (
    <Marker variant="border">
      <MarkerIcon>
        <InfoIcon />
      </MarkerIcon>
      <MarkerContent>
        Called <code>{block.name}</code>
      </MarkerContent>
    </Marker>
  )
}

function renderContent(msg: ChatMessage, align: "start" | "end") {
  if (typeof msg.content === "string") {
    return <TextBlockBubble text={msg.content} align={align} />
  }
  if (!Array.isArray(msg.content)) return null
  return (
    <>
      {msg.content.map((block: any, i: number) => {
        if (block.type === "text") return <TextBlockBubble key={i} text={block.text} align={align} />
        if (block.type === "tool_use") return <ToolUseMarker key={i} block={block} />
        return null
      })}
    </>
  )
}

function PendingApprovalNotice({ name }: { name: string }) {
  return (
    <Marker variant="border" className="text-foreground">
      <MarkerIcon>
        <InfoIcon />
      </MarkerIcon>
      <MarkerContent>
        Review before sending — approve <code>{name}</code> to send your message.
      </MarkerContent>
    </Marker>
  )
}

function RegistrySignInGate({ entityName }: { entityName: string }) {
  const [mode, setMode] = useState<'signin' | 'signup'>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)
    try {
      if (mode === 'signup') {
        await createUserWithEmailAndPassword(auth, email, password)
      } else {
        await signInWithEmailAndPassword(auth, email, password)
      }
    } catch (err: any) {
      if (err.code === 'auth/invalid-credential') {
        setError('Incorrect email or password.')
      } else if (err.code === 'auth/email-already-in-use') {
        setError('An account already exists for that email.')
        setMode('signin')
      } else if (err.code === 'auth/weak-password') {
        setError('Password should be at least 6 characters.')
      } else {
        setError(err.message || `Failed to ${mode === 'signup' ? 'create account' : 'sign in'}.`)
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="rounded-2xl border border-brand-border bg-brand-white p-6">
      <p className="text-sm font-medium text-brand-black mb-1">Sign in to chat with {entityName}</p>
      <p className="text-xs text-brand-gray mb-4 leading-relaxed">
        This confirms who you are so {entityName} knows a real person reached out. It doesn't create a business profile — you're just messaging them.
      </p>

      <form onSubmit={handleSubmit}>
        <FieldGroup>
          <FieldError errors={error ? [{ message: error }] : undefined} />

          <Field>
            <FieldLabel htmlFor="registry-chat-email">Email</FieldLabel>
            <Input
              id="registry-chat-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoComplete="username"
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="registry-chat-password">Password</FieldLabel>
            <Input
              id="registry-chat-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              minLength={mode === 'signup' ? 6 : undefined}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            />
          </Field>

          <Field>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full rounded-lg bg-brand-black px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-charcoal disabled:opacity-50"
            >
              {isLoading ? (mode === 'signup' ? 'Creating account…' : 'Signing in…') : (mode === 'signup' ? 'Create account & continue' : 'Sign in & continue')}
            </button>
          </Field>
        </FieldGroup>
      </form>

      <p className="mt-3 text-center text-xs text-brand-gray">
        {mode === 'signup' ? (
          <>Already have an account? <button type="button" onClick={() => { setMode('signin'); setError(null) }} className="underline">Sign in</button></>
        ) : (
          <>No account yet? <button type="button" onClick={() => { setMode('signup'); setError(null) }} className="underline">Create one</button></>
        )}
      </p>
    </div>
  )
}

export function RegistryChat({ targetEntityId, entityName }: { targetEntityId: string; entityName: string }) {
  const [isSignedIn, setIsSignedIn] = useState<boolean | null>(null)

  useEffect(() => {
    const unsubscribe = subscribeToAuthChanges((user) => {
      setIsSignedIn(!!user)
    })
    return unsubscribe
  }, [])

  const { conversation, pendingApproval, isLoading, error, sendMessage, resolveApproval } =
    useDashboardChat(undefined, { mode: 'counterparty', targetEntityId })

  const [draft, setDraft] = useState("")
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false)

  const handleSend = () => {
    const text = draft.trim()
    if (!text || isLoading) return
    setDraft("")
    sendMessage(text)
  }

  const handleDiscardConfirmed = () => {
    setDiscardConfirmOpen(false)
    resolveApproval(false)
  }

  if (isSignedIn === null) {
    return (
      <div className="flex h-32 items-center justify-center rounded-2xl border border-brand-border bg-brand-white text-sm text-brand-gray">
        Checking your session…
      </div>
    )
  }

  if (isSignedIn === false) {
    return <RegistrySignInGate entityName={entityName} />
  }

  return (
    <div className="flex h-128 flex-col rounded-2xl border border-brand-border bg-brand-white">
      <div className="shrink-0 border-b border-brand-border px-4 py-3">
        <p className="text-sm font-medium text-brand-black">Chat with {entityName}</p>
        <p className="text-xs text-brand-gray">Powered by FDW — no account needed beyond signing in once.</p>
      </div>

      <MessageScrollerProvider>
        <MessageScroller className="flex-1">
          <MessageScrollerViewport>
            <MessageScrollerContent className="px-4 py-4">
              {conversation.length === 0 && (
                <p className="text-sm text-brand-gray">
                  Tell us what you need — we'll help you reach {entityName}.
                </p>
              )}
              {conversation.map((msg, i) => {
                const align = msg.role === "user" ? "end" : "start"
                return (
                  <MessageScrollerItem key={i}>
                    <MessageGroup>
                      <Message align={align}>
                        <MessageContent>{renderContent(msg, align)}</MessageContent>
                      </Message>
                    </MessageGroup>
                  </MessageScrollerItem>
                )
              })}
              {pendingApproval && (
                <MessageScrollerItem>
                  <div className="space-y-2">
                    <PendingApprovalNotice name={pendingApproval.name} />
                    <div className="flex gap-2">
                      <button
                        onClick={() => resolveApproval(true)}
                        className="rounded-lg bg-brand-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-accentHover"
                      >
                        Send it
                      </button>
                      <button
                        onClick={() => setDiscardConfirmOpen(true)}
                        className="rounded-lg px-3 py-1.5 text-xs text-brand-charcoal hover:bg-brand-surface"
                      >
                        Don't send
                      </button>
                    </div>
                  </div>
                </MessageScrollerItem>
              )}
              {error && (
                <MessageScrollerItem>
                  <Marker variant="border" className="text-destructive">
                    <MarkerContent>{error}</MarkerContent>
                  </Marker>
                </MessageScrollerItem>
              )}
            </MessageScrollerContent>
          </MessageScrollerViewport>
          <MessageScrollerButton />
        </MessageScroller>
      </MessageScrollerProvider>

      <div className="flex shrink-0 items-end gap-2 border-t border-brand-border p-3">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault()
              handleSend()
            }
          }}
          placeholder={`Message ${entityName}...`}
          rows={1}
          disabled={isLoading || !!pendingApproval}
          className="max-h-32 min-h-9 flex-1 resize-none rounded-lg border border-brand-border bg-transparent px-3 py-2 text-sm outline-none disabled:opacity-50"
        />
        <Button onClick={handleSend} disabled={isLoading || !draft.trim() || !!pendingApproval} size="sm">
          {isLoading ? <Loader2Icon className="animate-spin" /> : "Send"}
        </Button>
      </div>

      {/* Shared confirm dialog for discarding a pending drafted message —
          same one-instance, boolean-driven pattern DashboardOutcomeDetail.tsx
          uses for outcome/rule deletion. */}
      <AlertDialog open={discardConfirmOpen} onOpenChange={setDiscardConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard this message?</AlertDialogTitle>
            <AlertDialogDescription>
              This message won't be sent to {entityName}. You'll need to write it again if you change your mind.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={handleDiscardConfirmed}>
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}